import React from 'react';
import {
  Form,
  FormInstance,
  TextInput,
  FormErrors,
  TextArea,
  SelectInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../grid';
import { useMetaData } from '../../../contexts/metadata';
import { CardBlock } from '../card';
import DOBInput from '../schedule/dob';
import TagsInput from '../schedule/tags';
import {
  GetCaregiverPhone,
  GetPatientPhone,
  IPatient,
  PatientFormData
} from '../../../libs/patient';
import { TagType } from '../../../libs/tag';
import Checkbox from '../../atoms/checkbox';

export const GetPatientData = (patient: IPatient): PatientFormData => {
  return {
    id: patient.id,
    firstName: patient.firstName,
    middleInitial: patient.middleInitial || '',
    lastName: patient.lastName,
    phone: GetPatientPhone(patient.phone, patient.phoneIsCaregiver),
    caregiverPhone: GetCaregiverPhone(
      patient.phone,
      patient.phoneIsCaregiver,
      patient.caregiverPhone
    ),
    mrn: patient.mrn || '',
    email: patient.email || '',
    dob: patient.dob || '',
    gender: patient.gender || '',
    note: patient.note || '',
    tags: patient.tags || '',
    csn: patient.csn || '',
    ccmEligible: patient.ccmEligible || false
  };
};

interface PatientFormProps {
  form: FormInstance<PatientFormData>;
  onSubmit: (data: PatientFormData) => any;
}

const PatientForm: React.FC<PatientFormProps> = ({
  form,
  onSubmit
}): JSX.Element => {
  const { terms } = useMetaData();
  const { account } = useMetaData();
  const validate = (data: PatientFormData): FormErrors => {
    const errors: FormErrors = {};
    if (data.phone) {
      const phone = data.phone.replace(/\D+/gi, '');
      if (phone.length !== 10) {
        errors.phone = 'The phone number is not in valid format!';
      }
    }
    if (data.caregiverPhone) {
      const phone = data.caregiverPhone.replace(/\D+/gi, '');
      if (phone.length !== 10) {
        errors.caregiverPhone = 'The phone number is not in valid format!';
      }
    }
    if (!data.phone && !data.caregiverPhone) {
      errors.phone = `A ${terms.patient.default} or caregiver phone number is required!`;
    }
    return errors;
  };
  return (
    <Form form={form} validate={validate} onSubmit={onSubmit}>
      <CardBlock>
        <Row>
          <Col md={10}>
            <TextInput label="First Name" name="firstName" required />
          </Col>
          <Col md={4}>
            <TextInput label="M. I." name="middleInitial" />
          </Col>
          <Col md={10}>
            <TextInput label="Last Name" name="lastName" required />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextInput label="Phone" name="phone" placeholder="XXX-XXX-XXXX" />
          </Col>
          <Col md={12}>
            <DOBInput label="Date of Birth (MM/DD/YYYY)" name="dob" />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextInput
              label="Caregiver Phone"
              name="caregiverPhone"
              placeholder="XXX-XXX-XXXX"
            />
          </Col>
          <Col md={12}>
            <SelectInput
              label="Gender"
              name="gender"
              options={[
                {
                  value: '',
                  label: '-- Select One --'
                },
                {
                  value: 'Male',
                  label: 'Male'
                },
                {
                  value: 'Female',
                  label: 'Female'
                },
                {
                  value: 'Unknown',
                  label: 'Unknown'
                },
                {
                  value: 'Other',
                  label: 'Other'
                },
                {
                  value: 'Nonbinary',
                  label: 'Nonbinary'
                }
              ]}
              fill
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextInput label={terms.mrn.capitalized} name="mrn" />
          </Col>
          <Col md={12}>
            <TextInput label="CSN (Encounter #)" name="csn" />
          </Col>
        </Row>
        {account?.isTimeTrackedEnabled ? (
          <Row>
            <Col>
              <Checkbox
                label="Consented to CCM"
                name="ccmEligible"
                aria-label="Consented to CCM"
                className="mt-2"
              />
            </Col>
          </Row>
        ) : null}
        <TagsInput label="Tags" name="tags" type={TagType.Patient} fill />
        <TextArea
          style={{
            height: '100px'
          }}
          label="Notes"
          name="note"
          fill
        />
      </CardBlock>
    </Form>
  );
};

export default PatientForm;
